import React, { useState } from 'react'
import { createZustandContext, createUseClientState, sanitizeFilter } from 'state/utilities'
import { createStore } from 'zustand'
import { getInitialClientState, getFilter, sortingOptions } from './helpers'
import { pick } from 'utilities/object'
import isEqual from 'react-fast-compare'

const UsersClientStateContext = createZustandContext('Users')

const UsersClientStateProvider = ({ children, ...props }) => {
    const [initialProps] = useState(props)

    const [store] = useState(createStore(set => {
        const initialClientState = getInitialClientState(initialProps)

        return {
            ...initialClientState,

            setQueryOptions: queryOptions => set(state => ({
                queryOptions: {
                    ...(state.queryOptions ?? null),
                    ...queryOptions
                }
            })),

            getInitialFilter: (wrapped = false) => {
                const source = getFilter(initialProps)

                return wrapped ?
                    pick(source, 'filter') :
                    source.filter
            },

            resetFilter: () => set(state => state.getInitialFilter(true)),

            setFilter: filter => set(state => {
                const { filter: initial = null } = state.getInitialFilter(true)

                const previous = !!state.filter ?
                    sanitizeFilter(state.filter) :
                    state.filter

                const next = sanitizeFilter({
                    ...initial,
                    ...filter
                })

                const changed = !isEqual(previous, next)

                return {
                    ...(changed ? { filter: next } : null),
                    filterApplied: !isEqual(initial, next)
                }
            }),

            getInitialSorting: () => pick(initialProps, 'sorting'),
            resetSorting: () => set(state => state.getInitialSorting()),

            toggleSorting: field => {
                if(!(field in sortingOptions)) {
                    return
                }

                set(state => {
                    const sorting = {
                        orderBy: field,
                        orderDirection: (state.sorting.orderBy === field) ?
                            (state.sorting.orderDirection === 'asc' ? 'desc' : 'asc') :
                            sortingOptions[field]
                    }

                    return { sorting }
                })
            }
        }
    }))

    return (
        <UsersClientStateContext.Provider value={store}>
            {children}
        </UsersClientStateContext.Provider>
    )
}

export const useUsersClientState = createUseClientState(UsersClientStateContext)

export * from './mutators'
export * from './helpers'
export default UsersClientStateProvider